'use client';

import { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaSearch, FaCar, FaBus, FaWalking, FaDirections } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';
import Card from '../ui/Card';

// Define the coordinates and address for Mikhael Homestay
const MIKHAEL_HOMESTAY = {
  lat: 2.540996898597396, // Actual coordinates
  lng: 102.78898272457076, // Actual coordinates
  address: "16, Jalan Sejati 6, Taman Tasik Sejati, 85000 Segamat, Johor, Malaysia"
};

// Define types for our component
interface TravelInfo {
  distance: string;
  duration: string;
  loading: boolean;
  error: string | null;
}

export default function FindUsEasily() {
  // State for the search input
  const [searchInput, setSearchInput] = useState("");
  const [origin, setOrigin] = useState<google.maps.LatLngLiteral | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  
  // State for travel information
  const [carInfo, setCarInfo] = useState<TravelInfo>({
    distance: "",
    duration: "",
    loading: false,
    error: null
  });
  
  const [transitInfo, setTransitInfo] = useState<TravelInfo>({
    distance: "",
    duration: "",
    loading: false,
    error: null
  });

  // Refs for DOM elements
  const mapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsScriptLoaded(true);
      return;
    }

    const googleMapScript = document.createElement('script');
    googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBeyimd2oEdb9tztGHBMjNSnAet-rPPGLs&libraries=places`;
    googleMapScript.async = true;
    googleMapScript.defer = true;
    googleMapScript.onload = () => {
      setIsScriptLoaded(true);
    };
    document.body.appendChild(googleMapScript);

    return () => {
      document.body.removeChild(googleMapScript);
    };
  }, []);

  // Initialize map once script is loaded
  useEffect(() => {
    if (!isScriptLoaded || !mapRef.current) return;

    try {
      // Initialize the map
      const newMap = new google.maps.Map(mapRef.current, {
        center: MIKHAEL_HOMESTAY,
        zoom: 15,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
      });
      
      // Add marker for Mikhael Homestay
      new google.maps.Marker({
        position: MIKHAEL_HOMESTAY,
        map: newMap,
        title: "Mikhael Homestay",
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
        }
      });

      // Initialize directions renderer
      const renderer = new google.maps.DirectionsRenderer({
        map: newMap,
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: "#4285F4",
          strokeWeight: 5,
        }
      });

      setMap(newMap);
      setDirectionsRenderer(renderer);

      // Initialize autocomplete if input is available
      if (inputRef.current) {
        const autoComplete = new google.maps.places.Autocomplete(inputRef.current);
        autoComplete.addListener('place_changed', () => {
          const place = autoComplete.getPlace();
          if (place.geometry && place.geometry.location) {
            setOrigin({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            });
            setSearchInput(place.formatted_address || '');
          }
        });
        setAutocomplete(autoComplete);
      }
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [isScriptLoaded]);

  // Function to calculate route and travel information
  const calculateRoute = () => {
    if (!origin || !map || !directionsRenderer) {
      return;
    }

    // Set loading states
    setCarInfo(prev => ({ ...prev, loading: true, error: null }));
    setTransitInfo(prev => ({ ...prev, loading: true, error: null }));

    // Calculate driving directions
    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: origin,
        destination: MIKHAEL_HOMESTAY,
        travelMode: google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          directionsRenderer.setDirections(result);
          
          // Get distance and duration
          const route = result.routes[0];
          if (route && route.legs[0]) {
            setCarInfo({
              distance: route.legs[0].distance?.text || "Unknown",
              duration: route.legs[0].duration?.text || "Unknown",
              loading: false,
              error: null
            });
          }
        } else {
          setCarInfo({
            distance: "",
            duration: "",
            loading: false,
            error: "Could not calculate driving directions."
          });
        }
      }
    );

    // Calculate transit directions
    directionsService.route(
      {
        origin: origin,
        destination: MIKHAEL_HOMESTAY,
        travelMode: google.maps.TravelMode.TRANSIT
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          // Don't render transit directions on the map, just get the info
          const route = result.routes[0];
          if (route && route.legs[0]) {
            setTransitInfo({
              distance: route.legs[0].distance?.text || "Unknown",
              duration: route.legs[0].duration?.text || "Unknown",
              loading: false,
              error: null
            });
          }
        } else {
          // If transit directions fail, try walking directions
          directionsService.route(
            {
              origin: origin,
              destination: MIKHAEL_HOMESTAY,
              travelMode: google.maps.TravelMode.WALKING
            },
            (walkResult, walkStatus) => {
              if (walkStatus === google.maps.DirectionsStatus.OK && walkResult) {
                const route = walkResult.routes[0];
                if (route && route.legs[0]) {
                  setTransitInfo({
                    distance: route.legs[0].distance?.text || "Unknown",
                    duration: `${route.legs[0].duration?.text || "Unknown"} (walking)`,
                    loading: false,
                    error: null
                  });
                }
              } else {
                setTransitInfo({
                  distance: "",
                  duration: "",
                  loading: false,
                  error: "Public transport not available for this route."
                });
              }
            }
          );
        }
      }
    );
  };

  return (
    <>
      <SectionHeading 
        title="Find Us Easily" 
        subtitle="Plan your journey to Mikhael Homestay with our interactive map and travel time calculator"
      />
      
      <motion.div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left side - Map and search */}
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Search bar */}
            <Card className="mb-6 p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-gray-400" />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Enter your starting location"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-all"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <motion.button
                  onClick={calculateRoute}
                  disabled={!origin}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg shadow-soft disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaDirections className="text-white" />
                  <span>Calculate</span>
                </motion.button>
              </div>
            </Card>
            
            {/* Map */}
            <motion.div 
              className="relative w-full h-[450px] rounded-xl overflow-hidden shadow-soft"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {!isScriptLoaded ? (
                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <p className="text-gray-500">Loading map...</p>
                </div>
              ) : null}
              <div ref={mapRef} className="w-full h-full" />
            </motion.div>
          </motion.div>
          
          {/* Right side - Location and travel info */}
          <motion.div 
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {/* Our Location */}
            <Card className="mb-6 bg-gradient-to-br from-blue-50 to-blue-100 border-none">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-soft">
                    <FaMapMarkerAlt className="text-white text-lg" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-gray-900">Our Location</h3>
                  <p className="text-gray-700 mt-2">{MIKHAEL_HOMESTAY.address}</p>
                </div>
              </div>
            </Card>
            
            {/* By Car */}
            <Card className="mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-soft">
                    <FaCar className="text-white text-lg" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">By Car</h3>
                  {carInfo.loading ? (
                    <p className="text-gray-500 mt-2">Calculating...</p>
                  ) : carInfo.error ? (
                    <p className="text-red-500 mt-2">{carInfo.error}</p>
                  ) : carInfo.distance && carInfo.duration ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-gray-700">Distance: <span className="font-medium text-blue-700">{carInfo.distance}</span></p>
                      <p className="text-gray-700">Duration: <span className="font-medium text-blue-700">{carInfo.duration}</span></p>
                    </div>
                  ) : (
                    <p className="text-gray-500 mt-2">Enter your starting location and click Calculate</p>
                  )}
                </div>
              </div>
            </Card>
            
            {/* Public Transport */}
            <Card>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-soft">
                    <FaBus className="text-white text-lg" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">Public Transport</h3>
                  {transitInfo.loading ? (
                    <p className="text-gray-500 mt-2">Calculating...</p>
                  ) : transitInfo.error ? (
                    <p className="text-red-500 mt-2">{transitInfo.error}</p>
                  ) : transitInfo.distance && transitInfo.duration ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-gray-700">Distance: <span className="font-medium text-blue-700">{transitInfo.distance}</span></p>
                      <p className="text-gray-700">Duration: <span className="font-medium text-blue-700">{transitInfo.duration}</span></p>
                    </div>
                  ) : (
                    <p className="text-gray-500 mt-2">Enter your starting location and click Calculate</p>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
        
        <motion.div 
          className="mt-8 p-5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p className="text-sm text-blue-800 flex items-center gap-2">
            <span className="bg-blue-200 p-1 rounded-full">
              <FaMapMarkerAlt className="text-blue-700" />
            </span>
            <strong>Note:</strong> Travel times are estimates only. For the most accurate directions, we recommend using Google Maps or Waze when actually traveling.
          </p>
        </motion.div>
      </motion.div>
    </>
  );
}
